import Graph from "../Graph";
import React, { useMemo, useState } from "react";
import Icon from "./Icon";
import "../../pages/InsightsTab.css";

function fmtPkr(val) {
  return Number(val || 0).toLocaleString("en-US");
}

/* One pill colour per Category-dropdown value, so a mixed "All Categories" list is
   still readable at a glance. Energy uses the base `.category` (teal) because the
   stylesheet only ships seven modifier classes for eight categories. */
const CATEGORY_PILL_CLASS = {
  "Voltage": "category-blue",
  "Current": "category-violet",
  "Power": "category-orange",
  "Power Factor": "category-green",
  "Energy": "",
  "Frequency": "category-pink",
  "THD Voltage": "category-indigo",
  "THD Current": "category-rose",
};

/* The three components of the General Loss Calculation Framework:
   Total = (WE x PR) + (PV x D) + (MC x PM). All three are listed so the parts
   visibly add up to the total; the ones a detector leaves at zero are dropped. */
const LOSS_COMPONENTS = [
  { key: "equipmentLoss", label: "Equipment" },
  { key: "productionLoss", label: "Downtime" },
  { key: "maintenanceLoss", label: "Maintenance" },
];

/* The two THD detectors both report category "Power Quality", so the row's own
   `category` cannot tell them apart. `categoryKey` is the label the combined
   endpoint stamps on, and it matches the Category dropdown exactly. */
export function categoryLabel(insight) {
  return insight?.categoryKey || insight?.category || "";
}

function SeverityPill({ severity }) {
  const isCrit = severity === "Critical";
  return (
    <span className={`pill ${isCrit ? "critical" : "warning"}`}>
      <span className="tiny-dot" />
      {severity}
    </span>
  );
}

function CategoryPill({ insight }) {
  const label = categoryLabel(insight);
  const modifier = CATEGORY_PILL_CLASS[label] || "";
  return <span className={`category ${modifier}`.trim()}>{label}</span>;
}

/* Detail Modal Popup with solid white background and dark backdrop */
function InsightModal({ insight, onClose }) {
  if (!insight) return null;

  const points = insight.chartData || [];
  const lossParts = LOSS_COMPONENTS.filter(({ key }) => Number(insight[key]) > 0);

  return (
    <div className="insights-overlay">
      <div className="detail-backdrop" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <div className="modal-head-row">
            <div>
              <div className="modal-title-row" style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                <CategoryPill insight={insight} />
                <SeverityPill severity={insight.severity} />
              </div>
              <h2>{insight.name}</h2>
              <div className="modal-machine">
                {insight.machine} • Detected: <strong>{insight.detected}</strong>
              </div>
            </div>
            <button className="close-btn" onClick={onClose} aria-label="Close modal">
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>

        <div className="modal-body">
          <p style={{ margin: 0, fontSize: "14px", color: "#475569", lineHeight: "1.5" }}>{insight.description}</p>

          {/* Per-machine numbers behind the detection: rated power, sanctioned load,
              wasted kWh. Without this the thresholds are unverifiable from the UI.
              Only the Power and Energy detectors fill it in. */}
          {insight.detail && (
            <p style={{ margin: 0, fontSize: "13px", color: "#0f172a", fontWeight: 600, lineHeight: "1.5" }}>
              {insight.detail}
            </p>
          )}

          {/* Financial Loss Card */}
          <div style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "14px 18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div>
              <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Estimated Financial Loss</div>
              <div style={{ fontSize: "22px", fontWeight: "800", color: "#dc2626", marginTop: "4px" }}>PKR {fmtPkr(insight.loss)}</div>
            </div>
            <div style={{ fontSize: "12px", textAlign: "right", color: "#475569", lineHeight: "1.6" }}>
              {lossParts.map(({ key, label }) => (
                <div key={key}>
                  {label}: <strong>PKR {fmtPkr(insight[key])}</strong>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Trend Chart */}
          {points.length > 0 && (() => {
            const hasThreshold = points.some((p) => p.threshold !== undefined && p.threshold !== null);
            const datasets = [
              {
                label: insight.chartParam || "Measured Value",
                data: points.map((p) => p.value),
                borderColor: "#1BA08E",
                backgroundColor: "rgba(27, 160, 142, 0.12)",
                fill: true,
                tension: 0.3,
                pointRadius: 3,
                borderWidth: 2,
              },
            ];

            if (hasThreshold) {
              datasets.push({
                label: "Threshold Limit",
                data: points.map((p) => p.threshold),
                borderColor: "#ef4444",
                borderDash: [5, 4],
                pointRadius: 0,
                fill: false,
                borderWidth: 2,
              });
            }

            const unitSuffix = insight.chartUnit ? ` ${insight.chartUnit}` : "";
            const currentThreshold = insight.severity === "Critical" && insight.criticalThreshold
              ? insight.criticalThreshold
              : insight.warningThreshold;

            return (
              <div className="section">
                <p className="section-title">
                  {insight.chartParam}{insight.chartUnit ? ` (${insight.chartUnit})` : ""}
                  {currentThreshold ? ` • Threshold: ${currentThreshold}` : ""}
                </p>
                <div style={{ background: "#fff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                  <div style={{ height: "220px", width: "100%", marginTop: "10px" }}>
                    <Graph
                      labels={points.map((p, i) => p.time || p.timestamp || `Point ${i + 1}`)}
                      datasets={datasets}
                      valueFormatter={(v) =>
                        Number.isFinite(v)
                          ? `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 3 })}${unitSuffix}`
                          : `${v || ""}${unitSuffix}`
                      }
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: hasThreshold,
                            position: "top",
                            align: "end",
                            labels: {
                              boxWidth: 12,
                              font: { size: 11, weight: 600 },
                            },
                          },
                        },
                        scales: {
                          x: { ticks: { maxTicksLimit: 8 } },
                          y: {
                            title: { display: Boolean(insight.chartUnit), text: insight.chartUnit || "" },
                            beginAtZero: false,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Likely Root Causes */}
          <div className="section">
            <p className="section-title">Likely Root Causes</p>
            <ul className="bullet-list">
              {insight.reasons?.map((r, i) => (
                <li key={i} className="bullet-item">
                  <span className="reason-bullet" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Recommended Fixes */}
          <div className="section">
            <p className="section-title">Recommended Fixes</p>
            <ul className="bullet-list">
              {insight.fixes?.map((f, i) => (
                <li key={i} className="bullet-item">
                  <Icon name="check" className="tick-icon" size={14} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {insight.occurrences?.length > 1 && (
            <div className="section">
              <p className="section-title">Occurrence Windows</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                {insight.occurrences.map((o, i) => (
                  <span key={i} className="ghost-btn" style={{ height: "auto", padding: "4px 8px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <Icon name="clock" size={12} /> {o}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders whatever insights it is handed. All fetching, filtering and totalling now
 * happens once in EnSightTab against the single `/ensight/ensight_data` response, so
 * this component is purely presentational and works for one category or all eight.
 */
export default function InsightsTable({ insights, summary, loading, error, emptyTitle, emptyDesc }) {
  const [modalId, setModalId] = useState(null);

  const activeModalInsight = useMemo(
    () => insights.find((i) => i.id === modalId) || null,
    [insights, modalId]
  );

  if (loading) {
    return (
      <div className="panel" style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#64748b", fontWeight: 600 }}>Loading enSight Insights...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel empty-state">
        <div>
          <div className="empty-icon"><Icon name="info" size={28} /></div>
          <p className="empty-title">{error}</p>
          <p className="empty-desc">Check backend connection and press Apply to retry</p>
        </div>
      </div>
    );
  }

  if (!insights.length) {
    return (
      <div className="panel empty-state">
        <div>
          <div className="empty-icon"><Icon name="info" size={28} /></div>
          <p className="empty-title">{emptyTitle}</p>
          <p className="empty-desc">{emptyDesc}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="panel table-wrap">
        <div className="table-head">
          <p className="table-title">{insights.length} insight{insights.length !== 1 ? "s" : ""} detected</p>
          <p className="table-sub">Sorted: Critical first</p>
        </div>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>Insight</th>
                <th>Machine</th>
                <th>Category</th>
                <th>Severity</th>
                <th>Detected</th>
                <th style={{ textAlign: "right" }}>Loss (PKR)</th>
                <th style={{ textAlign: "center" }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {insights.map((row) => (
                <tr key={row.id}>
                  <td style={{ boxShadow: `inset 3px 0 0 ${row.severity === "Critical" ? "#ef4444" : "#f59e0b"}` }}>
                    {row.name}
                  </td>
                  <td>{row.machine}</td>
                  <td><CategoryPill insight={row} /></td>
                  <td><SeverityPill severity={row.severity} /></td>
                  <td style={{ color: "#64748b", fontSize: 12 }}>{row.detected}</td>
                  <td className="numeric">
                    {row.loss > 0 ? fmtPkr(row.loss) : <span className="muted-cell">—</span>}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button className="action-btn" onClick={() => setModalId(row.id)}>
                      <Icon name="eye" size={14} />
                      <span style={{ marginLeft: 6 }}>View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals">
                <td colSpan={5} style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Totals ({insights.length} rows)
                </td>
                <td className="numeric" style={{ fontWeight: 900, color: "#334155" }}>{fmtPkr(summary.totalLoss)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {modalId && <InsightModal insight={activeModalInsight} onClose={() => setModalId(null)} />}
    </>
  );
}
